import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/common/SEOHead';
import InternalLinks from '@/components/seo/InternalLinks';
import { TrendingUp, BarChart3, Sparkles, Target } from 'lucide-react';

const API_BASE = '';

export default function TrendPage({ trendType }) {
  const { slug } = useParams();
  const { currentLang } = useLanguage();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const scope = slug ? (trendType?.includes('team') ? 'team' : 'league') : 'global';

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ scope, slug: slug || '', lang: currentLang });
    fetch(`${API_BASE}/api/seo/trends/${trendType}?${params}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [trendType, slug, currentLang, scope]);

  if (loading) return <div className="container mx-auto px-4 py-8 text-slate-400">Loading...</div>;
  if (!data || data.error) return <div className="container mx-auto px-4 py-8 text-slate-400">{t('common.notFound')}</div>;

  return (
    <div className="container mx-auto px-4 max-w-7xl py-8" data-testid="trend-page">
      <SEOHead title={data.title} description={data.meta_description} />

      <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
        {data.h1 || data.title}
      </h1>

      {data.intro && data.intro.length > 20 && (
        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-5 mb-6">
          <p className="text-sm text-slate-400 leading-relaxed">{data.intro}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {data.trend_summary && data.trend_summary.length > 20 && (
          <div className="border border-emerald-500/20 bg-emerald-500/5 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">Trend Analysis</h3>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">{data.trend_summary}</p>
          </div>
        )}
        {data.key_stats && data.key_stats.length > 20 && (
          <div className="border border-blue-500/20 bg-blue-500/5 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">Key Stats</h3>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">{data.key_stats}</p>
          </div>
        )}
        {data.insight && data.insight.length > 20 && (
          <div className="border border-violet-500/20 bg-violet-500/5 rounded-xl p-4 md:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-violet-400" />
              <h3 className="text-sm font-semibold text-violet-400 uppercase tracking-wider">Insight</h3>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed font-medium">{data.insight}</p>
          </div>
        )}
      </div>
    </div>
  );
}
